import { useMemo } from "react";
import { bool, object, func, number } from "prop-types";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { hashCode } from "@repo/utils";
import {
  PinnableCell,
  DescriptionVariantSelector,
  useEstimateEditorSettings,
  usePresetFieldNames,
  useEstimateStructure,
  useVisibilityModeCellClassName,
} from "@tools";
import { WysiwygEditor } from "@components";
import { makeStyles } from "@material-ui/styles";
import classnames from "classnames";

export const TaskDescriptionCell = observer(({
  columnSettings,
  element,
  isSectionRow,
  onAfterChange,
  isLastPinnedCell,
  isLastPinnableCell,
  tableDepth,
}) => {
  const { t } = useTranslation();
  const { allowEdition, blockExpansions, isLibrary, isProposal, isPdfPrinterView } = useEstimateEditorSettings();
  const { getFieldName } = usePresetFieldNames(isProposal);
  const structure = useEstimateStructure();
  const className = useVisibilityModeCellClassName(columnSettings.hide);
  const classes = useStyle();
  
  const { searchPhrases } = structure;
  const { id: elementId, descriptions } = element;
  const { id, descriptionId, pinLeftPx } = columnSettings;
  
  const description = descriptions?.get(descriptionId);
  const content = description?.content;
  const showMore = description?.showMore;
  
  const presetText = useMemo(() => (
    showMore ? "pt-showLessContent" : "pt-showMoreContent"
  ), [ showMore ]);
  
  const handleChange = (value) => {
    if(value === content)
      return;
    
    element.setDescription(descriptionId, value);
    onAfterChange();
  }
  
  return (
    <PinnableCell
      resizeable
      widerPaddings
      colName={ id }
      allowOverflowDisplay={isLastPinnedCell}
      className={className}
      pinnedLeft={typeof pinLeftPx === "number"}
      tableDepth={tableDepth}
    >
      <WysiwygEditor
        readOnly={!allowEdition}
        blockContentButton={blockExpansions}
        noEmptyHtmlStrings
        changeOnClickAway
        placeholder={t("views.editor.desc_change")}
        onChange={handleChange}
        name={`desc${hashCode(elementId)}`}
        value={content}
        // pinToolbarToContainer
        shorten={!isPdfPrinterView}
        useBorder
        useBorderHover
        placeholderOnFocus
        showMore={showMore || isPdfPrinterView}
        onShowMore={description?.setShowMore}
        moreButtonContent={ getFieldName(presetText) }
        moreButtonClassName={classnames(
          presetText,
          !isSectionRow && "preset-moreDesc",
          isSectionRow && "preset-moreDescSection color-primary-lighter"
        )}
        className={classnames(
          "my-1-5",
          classes.editor,
          allowEdition && classes.editorEditable,
          !isLastPinnableCell && "bordered",
          !isLastPinnableCell && classes.bordered, //@note: will work until breakdown columns are reorderable
        )}
        containerClassName={allowEdition ? "" : "px-0"}
        editorStyle={{ minHeight: 24 }}
        navigationKey={ `bd-desc${descriptionId?.split("_")[1] || 0}` }
        searchPhrases={searchPhrases?.map(p => p.value)}
        toolbarExtension={
          !isLibrary &&
          <DescriptionVariantSelector/>
        }
      />
    </PinnableCell>
  )
})

TaskDescriptionCell.propTypes = {
  element: object.isRequired,
  columnSettings: object.isRequired,
  onAfterChange: func.isRequired,
  tableDepth: number.isRequired,
  isSectionRow: bool,
  isLastPinnedCell: bool,
  isLastPinnableCell: bool,
}

const useStyle = makeStyles((theme) => ({
  editor: {
    marginRight: -6,
    paddingRight: 4,
    width: "calc(100% + 6px)",
  },
  editorEditable: {
    marginLeft: -4,
    width: "calc(100% + 10px)",
  },
  bordered: {
    borderRightWidth: 1,
    borderRightStyle: "solid",
    borderRightColor: theme.props.border,
  }
}));